import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import Alert from 'react-bootstrap/lib/Alert';
import { useTranslation } from 'react-i18next';
import {
  loadRecentBatches,
  RecentSavedBatch,
} from '../utils/previous-values-manager';
import { formatTimestamp } from '../../../utils/date';
import { useStores } from '../../../hooks/use-stores';

interface BatchSelectionModalProps {
  show: boolean;
  onHide: () => void;
  currentValues: any;
  setValues: (values: any) => void;
  setInitialValues?: (values: any) => void;
  // Customization props
  title: string;
  submitButtonText: string;
  submitButtonStyle?: string;
  alertMessage?: React.ReactNode;
  alertStyle?: 'info' | 'warning' | 'danger' | 'success';
  onApplyBatch: (
    batchId: string,
    currentValues: any,
    setValues: (values: any) => void,
  ) => Promise<any>;
}

const BatchSelectionModal: React.FC<BatchSelectionModalProps> = ({
  show,
  onHide,
  currentValues,
  setValues,
  setInitialValues,
  title,
  submitButtonText,
  submitButtonStyle = 'primary',
  alertMessage,
  alertStyle = 'info',
  onApplyBatch,
}) => {
  const { t } = useTranslation();
  const { settingsStore } = useStores();
  const [recentBatches, setRecentBatches] = useState<RecentSavedBatch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      loadBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const batches = await loadRecentBatches(settingsStore);
      setRecentBatches(batches);
      if (batches.length > 0) {
        setSelectedBatchId(batches[0].id);
      }
    } catch (err) {
      console.error('Failed to load recent batches:', err);
    } finally {
      setLoading(false);
    }
  }, [settingsStore]);

  const handleApply = useCallback(async () => {
    if (!selectedBatchId) {
      return;
    }

    const calculatedValues = await onApplyBatch(
      selectedBatchId,
      currentValues,
      setValues,
    );

    if (calculatedValues && setInitialValues) {
      setInitialValues(calculatedValues);
    }

    onHide();
  }, [
    selectedBatchId,
    currentValues,
    setValues,
    setInitialValues,
    onHide,
    onApplyBatch,
  ]);

  return (
    <Modal show={show} onHide={onHide} bsSize="large">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {alertMessage && (
          <Alert bsStyle={alertStyle}>
            {alertMessage}
          </Alert>
        )}
        <FormGroup>
          <ControlLabel>
            {t('Select a recently completed batch to load values from:')}
          </ControlLabel>
          <FormControl
            componentClass="select"
            value={selectedBatchId}
            onChange={(e: any) => setSelectedBatchId(e.target.value)}
            disabled={loading || recentBatches.length === 0}
          >
            {recentBatches.length === 0 ? (
              <option value="">{t('No recent batches available')}</option>
            ) : (
              recentBatches.map(batch => {
                const details = [];
                if (batch.ticketId) details.push(`Ticket: ${batch.ticketId}`);
                if (batch.mixDescription) details.push(`Mix: ${batch.mixDescription}`);
                if (batch.truckId) details.push(`Truck: ${batch.truckId} (${batch.truckCode || 'No code'})`);
                if (batch.orderCode) details.push(`Order: ${batch.orderCode}`);
                const detailsStr = details.length > 0 ? ` (${details.join(', ')})` : '';

                return (
                  <option key={batch.id} value={batch.id}>
                    {batch.batchCode} - {formatTimestamp(batch.timestamp, t)}{detailsStr}
                  </option>
                );
              })
            )}
          </FormControl>
        </FormGroup>
        {recentBatches.length > 0 && (
          <div className="text-muted">
            <small>{t('Total batches available: {{count}}', { count: recentBatches.length })}</small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>{t('Cancel')}</Button>
        <Button
          bsStyle={submitButtonStyle}
          onClick={handleApply}
          disabled={!selectedBatchId || loading}
        >
          {submitButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BatchSelectionModal;
